import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import LoadingSpinner from "component/LoadingSpinner";
import ProjectService from "service/ProjectService";
import { ProjectDetailResp } from "domain/payload/response/project/ProjectDetailResp";
import * as XLSX from "xlsx";
import MultiSelectDropdown from "component/MultiSelectDropdown";
import { ProjectTaskConfigResp, ProjectTaskConfigJobResp, ProjectTaskConfigFieldUserResp } from 'domain/payload/response/project/ProjectTaskConfigResp';
import "bootstrap/dist/css/bootstrap.min.css";
import { ProjectTaskProceedJobAssignmentReq, ProjectTaskProceedJobAssignmentDataReq } from "domain/payload/request/project/ProjectTaskProceedJobAssignmentReq";
import CustomDropdown, { DropdownOption } from "component/CustomDropdown";
import { useNavPillsIndicator } from "hooks/useNavPillsIndicator";
import { PageSizeOption } from "domain/constant/PageSizeOption";

type AtomicAssignmentItem = {
  jobId?: string;
  jobPlaceRef?: string;
  userId?: string;
  userEmail?: string;
  action: string;
};

type QueueItem = {
  name: string;
  jobs: number;
  users: number;
  jobIds: string[];
  jobPlaceRefs?: string[];
  userIds: string[];
  userEmails?: string[];
  action: string;
  jobInputMethod?: "selection" | "id" | "placeRef";
  userInputMethod?: "selection" | "id" | "email";
};


const ProjectAdvanceAssignment: React.FC = () => {
  const { id } = useParams();
  const { profile } = useSelector((state: any) => state.auth);
  const projectService = ProjectService();

  const navigate = useNavigate();

  const [project, setProject] = useState<ProjectDetailResp | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [step, setStep] = useState<1 | 2 | 3>(1);

  const [method, setMethod] = useState<"xlsx" | "selected">("xlsx");
  const [uploadedData, setUploadedData] = useState<any[]>([]);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  const [jobInputMethod, setJobInputMethod] = useState<"selection" | "id" | "placeRef">("selection");
  const [jobs, setJobs] = useState<ProjectTaskConfigJobResp[]>([]);
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const [manualJobIds, setManualJobIds] = useState("");
  const [manualJobPlaceRefs, setManualJobPlaceRefs] = useState("");
  const [jobSearch, setJobSearch] = useState("");
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);

  const [userInputMethod, setUserInputMethod] = useState<"selection" | "id" | "email">("selection");
  const [fieldUsers, setFieldUsers] = useState<ProjectTaskConfigFieldUserResp[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [manualUserIds, setManualUserIds] = useState("");
  const [manualUserEmails, setManualUserEmails] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  const [queueName, setQueueName] = useState("");
  const [action, setAction] = useState("");
  const [queues, setQueues] = useState<QueueItem[]>([]);
  const [selectedQueueIndex, setSelectedQueueIndex] = useState<number | null>(null);
  const [isNextLoading, setIsNextLoading] = useState(false);

  const [previewData, setPreviewData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [filter, setFilter] = useState<"all" | "valid" | "invalid">("all");
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [isProceeding, setIsProceeding] = useState(false);

  const [previewProgress, setPreviewProgress] = useState<{ current: number; total: number } | null>(null);
  const [proceedProgress, setProceedProgress] = useState<{ current: number; total: number } | null>(null);

  const { navRef: jobInputMethodNavRef, indicatorStyle: jobInputMethodIndicatorStyle } = useNavPillsIndicator(jobInputMethod);
  const { navRef: userInputMethodNavRef, indicatorStyle: userInputMethodIndicatorStyle } = useNavPillsIndicator(userInputMethod);
  const ACTION_OPTIONS: DropdownOption[] = [
    { id: "", name: 'Select Action' },
    { id: "replace", name: 'Replace' },
    { id: "revoke", name: 'Revoke' },
  ];

  const invalidCount = previewData.filter((item) => item.checkValid === false).length;

  const MAX_TOTAL_ROWS = 2000
  const INITIAL_CHUNK_SIZE = 200;

  const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function isValidUuid(value?: string): boolean {
    return !!value && UUID_REGEX.test(value.trim());
  }

  function isValidEmail(value?: string): boolean {
    return !!value && EMAIL_REGEX.test(value.trim());
  }

  function splitAndValidate(
    raw: string,
    validator: (v: string) => boolean
  ): { valid: string[]; invalid: string[] } {
    const tokens = raw
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    const valid: string[] = [];
    const invalid: string[] = [];
    tokens.forEach((t) => (validator(t) ? valid.push(t) : invalid.push(t)));
    return { valid, invalid };
  }

  async function processInChunks<T, R>(
    items: T[],
    initialChunkSize: number,
    onProgress: (done: number, total: number) => void,
    chunkFn: (chunk: T[]) => Promise<R[]>
  ): Promise<R[]> {
    const total = items.length;
    let results: R[] = [];
    let i = 0;
    let chunkSize = initialChunkSize;

    if (total === 0) {
      onProgress(0, 0);
      return results;
    }

    while (i < total) {
      const chunk = items.slice(i, i + chunkSize);
      try {
        const chunkResult = await chunkFn(chunk);
        results = results.concat(chunkResult);
        i += chunk.length;
        onProgress(Math.min(i, total), total);
      } catch (err: any) {
        const isSizeLimitError =
          err?.response?.status === 400 &&
          /melebihi batas maksimum/i.test(err?.response?.data?.message || "");

        if (isSizeLimitError && chunkSize > 1) {
          chunkSize = Math.max(1, Math.floor(chunkSize / 2));
          continue;
        }
        throw err;
      }
    }

    return results;
  }

  function buildAtomicItemsFromQueues(queues: QueueItem[]): AtomicAssignmentItem[] {
    const items: AtomicAssignmentItem[] = [];

    queues.forEach((q) => {
      const jobRefs: { jobId?: string; jobPlaceRef?: string }[] = [];
      if (q.jobPlaceRefs && q.jobPlaceRefs.length > 0) {
        q.jobPlaceRefs.forEach((ref) => jobRefs.push({ jobPlaceRef: ref }));
      } else {
        (q.jobIds || []).forEach((jid) => jobRefs.push({ jobId: jid }));
      }

      const userRefs: { userId?: string; userEmail?: string }[] = [];
      if (q.userEmails && q.userEmails.length > 0) {
        q.userEmails.forEach((email) => userRefs.push({ userEmail: email }));
      } else {
        (q.userIds || []).forEach((uid) => userRefs.push({ userId: uid }));
      }

      jobRefs.forEach((jobRef) => {
        userRefs.forEach((userRef) => {
          items.push({ ...jobRef, ...userRef, action: q.action });
        });
      });
    });

    return items;
  }

  useEffect(() => {
    const fetchProjectDetail = async () => {
      const token = profile?.data?.token;
      if (!token || !id) {
        if (!token) window.alert('Token tidak ditemukan. Silakan login kembali.');
        return;
      }

      try {
        setIsLoading(true);
        const res = await projectService.projectDetail(token, id);
        setProject(res.data!);
      } catch (err: any) {
        console.error("Error fetching project detail:", err);
        const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil detail project';
        window.alert(`Error: ${errorMessage}`);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProjectDetail();
  }, [id, profile]);

  useEffect(() => {
    const fetchJobs = async () => {
      const token = profile?.data?.token;
      if (!token || jobInputMethod !== "selection") {
        if (!token && jobInputMethod === "selection") {
          window.alert('Token tidak ditemukan. Silakan login kembali.');
        }
        return;
      }

      try {
        setIsLoadingJobs(true);

        if (!id) return;

        if (jobSearch.trim()) {
          const res = await projectService.projectTaskConfigJob(
            token,
            id,
            jobSearch,
            1,
            9999
          );
          setJobs(res.data || []);
        } else {
          const res = await projectService.projectTaskConfigJob(
            token,
            id,
            null,
            1,
            9999
          );
          const allJobs = res.data || [];
          setJobs(allJobs.slice(0, 100));
        }
      } catch (err: any) {
        console.error("Error fetching jobs:", err);
        const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil daftar job';
        window.alert(`Error: ${errorMessage}`);
      } finally {
        setIsLoadingJobs(false);
      }
    };

    const timeout = setTimeout(fetchJobs, 400);
    return () => clearTimeout(timeout);
  }, [jobInputMethod, jobSearch, profile, id]);

  useEffect(() => {
    const fetchFieldUsers = async () => {
      const token = profile?.data?.token;
      if (!token || userInputMethod !== "selection") {
        if (!token && userInputMethod === "selection") {
          window.alert('Token tidak ditemukan. Silakan login kembali.');
        }
        return;
      }

      try {
        setIsLoadingUsers(true);

        if (userSearch.trim()) {
          const res = await projectService.projectTaskConfigFieldUser(
            token,
            userSearch,
            1,
            9999
          );
          setFieldUsers(res.data || []);
        } else {
          const res = await projectService.projectTaskConfigFieldUser(
            token,
            null,
            1,
            9999
          );
          const allUsers = res.data || [];
          setFieldUsers(allUsers.slice(0, 100));
        }
      } catch (err: any) {
        console.error("Error fetching users:", err);
        const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil daftar user';
        window.alert(`Error: ${errorMessage}`);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    const timeout = setTimeout(fetchFieldUsers, 400);
    return () => clearTimeout(timeout);
  }, [userInputMethod, userSearch, profile]);


  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const data = new Uint8Array(event.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

      const requiredHeaders = ["user_id", "job_id", "action"];
      const headers = Object.keys(jsonData[0] || {}).map((h) =>
        h.replace(/[\uFEFF\xA0]/g, "").trim().toLowerCase()
      );

      const missingHeaders = requiredHeaders.filter((h) => !headers.includes(h));

      if (missingHeaders.length > 0) {
        alert(`Header tidak valid. Pastikan ada: ${missingHeaders.join(", ")}`);
        e.target.value = "";
        setUploadedData([]);
        setUploadedFileName(null);
        return;
      }

      if (jsonData.length > MAX_TOTAL_ROWS) {
        alert(
          `Jumlah baris pada file (${jsonData.length}) melebihi batas maksimum ${MAX_TOTAL_ROWS} baris. Silakan kurangi jumlah data pada file lalu upload kembali.`
        );
        e.target.value = "";
        setUploadedData([]);
        setUploadedFileName(null);
        return;
      }

      setUploadedData(jsonData);
      setUploadedFileName(file.name);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDownloadTemplate = async () => {
    const token = profile?.data?.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }
    try {
      const response = await projectService.downloadTemplateAdvanceAssignment(token);
      window.alert(response?.message || 'Template sedang diproses dan akan dikirimkan ke email Anda.');
    } catch (err: any) {
      console.error("Download template failed:", err);
      const errorMessage = err.response?.data?.message || err.message || err || 'Gagal mengunduh template';
      window.alert(`Error: ${errorMessage}`);
    }
  };

  const handleJobInputMethodChange = (newMethod: "selection" | "id" | "placeRef") => {
    if (newMethod === jobInputMethod) return;

    if (jobInputMethod === "selection" && newMethod === "id") {
      setManualJobIds(selectedJobIds.join(", "));
    } else if (jobInputMethod === "selection" && newMethod === "placeRef") {
      const placeRefs = jobs
        .filter(job => selectedJobIds.includes(job.id))
        .map(job => job.placeReferenceId)
        .filter(ref => ref !== null && ref !== undefined);

      if (placeRefs.length > 0) {
        setManualJobPlaceRefs(placeRefs.join(", "));
      } else {
        setManualJobPlaceRefs("");
      }
    } else if (jobInputMethod === "id" && newMethod === "selection") {
      const ids = manualJobIds.split(",").map(id => id.trim()).filter(Boolean);
      setSelectedJobIds(ids);
    } else if (jobInputMethod === "id" && newMethod === "placeRef") {
      const ids = manualJobIds.split(",").map(id => id.trim()).filter(Boolean);
      const placeRefs = jobs
        .filter(job => ids.includes(job.id))
        .map(job => job.placeReferenceId)
        .filter(ref => ref !== null && ref !== undefined);

      if (placeRefs.length > 0) {
        setManualJobPlaceRefs(placeRefs.join(", "));
      } else {
        setManualJobPlaceRefs("");
      }
    } else if (jobInputMethod === "placeRef" && newMethod === "selection") {
      const placeRefs = manualJobPlaceRefs.split(",").map(ref => ref.trim()).filter(Boolean);
      const jobIds = jobs
        .filter(job => job.placeReferenceId && placeRefs.includes(job.placeReferenceId))
        .map(job => job.id);

      if (jobIds.length > 0) {
        setSelectedJobIds(jobIds);
      } else {
        setSelectedJobIds([]);
      }
    } else if (jobInputMethod === "placeRef" && newMethod === "id") {
      const placeRefs = manualJobPlaceRefs.split(",").map(ref => ref.trim()).filter(Boolean);
      const jobIds = jobs
        .filter(job => job.placeReferenceId && placeRefs.includes(job.placeReferenceId))
        .map(job => job.id);

      if (jobIds.length > 0) {
        setManualJobIds(jobIds.join(", "));
      } else {
        setManualJobIds("");
      }
    }

    setJobInputMethod(newMethod);
  };

  const handleUserInputMethodChange = (newMethod: "selection" | "id" | "email") => {
    if (newMethod === userInputMethod) return;

    if (userInputMethod === "selection" && newMethod === "email" && selectedUserIds.length > 0) {
      const selectedEmails = fieldUsers
        .filter(u => selectedUserIds.includes(u.id))
        .map(u => u.email)
        .filter(email => email !== null && email !== undefined && email.trim() !== "");

      if (selectedEmails.length > 0) {
        setManualUserEmails(selectedEmails.join(", "));
      } else {
        alert("User yang dipilih tidak memiliki email. Silakan pilih user dengan email yang valid.");
        return;
      }
    }

    if (userInputMethod === "selection" && newMethod === "id" && selectedUserIds.length > 0) {
      setManualUserIds(selectedUserIds.join(", "));
    }

    if (userInputMethod === "id" && newMethod === "selection" && manualUserIds.trim()) {
      const ids = manualUserIds.split(",").map(id => id.trim()).filter(Boolean);
      setSelectedUserIds(ids);
    }

    if (userInputMethod === "email" && newMethod === "selection" && manualUserEmails.trim()) {
      const emails = manualUserEmails.split(",").map(e => e.trim()).filter(Boolean);
      const matchingUsers = fieldUsers.filter(u =>
        u.email && emails.includes(u.email.trim())
      );
      setSelectedUserIds(matchingUsers.map(u => u.id));
    }

    if (userInputMethod === "email" && newMethod === "id" && manualUserEmails.trim()) {
      const emails = manualUserEmails.split(",").map(e => e.trim()).filter(Boolean);
      const matchingUserIds = fieldUsers
        .filter(u => u.email && emails.includes(u.email.trim()))
        .map(u => u.id);

      if (matchingUserIds.length > 0) {
        setManualUserIds(matchingUserIds.join(", "));
      }
    }

    if (userInputMethod === "id" && newMethod === "email" && manualUserIds.trim()) {
      const ids = manualUserIds.split(",").map(id => id.trim()).filter(Boolean);
      const matchingEmails = fieldUsers
        .filter(u => ids.includes(u.id))
        .map(u => u.email)
        .filter(email => email !== null && email !== undefined && email.trim() !== "");

      if (matchingEmails.length > 0) {
        setManualUserEmails(matchingEmails.join(", "));
      } else {
        alert("User ID yang dimasukkan tidak memiliki email. Silakan gunakan metode lain.");
        return;
      }
    }

    if (newMethod !== "id") {
      setManualUserIds("");
    }
    if (newMethod !== "email") {
      setManualUserEmails("");
    }
    if (newMethod !== "selection") {
      setSelectedUserIds([]);
    }

    setUserInputMethod(newMethod);
  };

  const cleanInputIds = (input: string): string => {
    if (!input) return "";
    const parts = input.split(/[\n\r\t,]+/);

    const cleaned = parts
      .map(part => part.trim())
      .filter(part => part.length > 0);

    return cleaned.join(", ");
  };

  const cleanPastedText = (text: string): string => {
    if (!text) return "";

    const normalized = text
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\t/g, ' ');

    const parts = normalized.split(/[\n,;|]+/);

    const cleaned = parts
      .map(part => part.trim())
      .filter(part => part.length > 0 && part !== '-');

    return cleaned.join(", ");
  };

  const handleAddQueue = () => {
    let finalJobIds: string[] = [];
    let finalJobPlaceRefs: string[] = [];
    let finalUserIds: string[] = [];
    let finalUserEmails: string[] = [];

    const validationErrors: string[] = [];

    if (jobInputMethod === "selection") {
      finalJobIds = [...selectedJobIds];
    } else if (jobInputMethod === "id") {
      const cleaned = cleanInputIds(manualJobIds);
      const { valid, invalid } = splitAndValidate(cleaned, isValidUuid);
      finalJobIds = valid;
      if (invalid.length > 0) {
        validationErrors.push(
          `Job ID tidak valid (harus UUID, contoh: 1bd8ed28-9304-4ac9-884c-90fbaf29ed28): ${invalid.join(", ")}`
        );
      }
    } else if (jobInputMethod === "placeRef") {
      const cleaned = cleanInputIds(manualJobPlaceRefs);
      finalJobPlaceRefs = cleaned ? cleaned.split(",").map(ref => ref.trim()).filter(Boolean) : [];
    }

    if (userInputMethod === "selection") {
      finalUserIds = [...selectedUserIds];
    } else if (userInputMethod === "id") {
      const cleaned = cleanInputIds(manualUserIds);
      const { valid, invalid } = splitAndValidate(cleaned, isValidUuid);
      finalUserIds = valid;
      if (invalid.length > 0) {
        validationErrors.push(`User ID tidak valid (harus UUID): ${invalid.join(", ")}`);
      }
    } else if (userInputMethod === "email") {
      const cleaned = cleanInputIds(manualUserEmails);
      const { valid, invalid } = splitAndValidate(cleaned, isValidEmail);
      finalUserEmails = valid;
      if (invalid.length > 0) {
        validationErrors.push(`Format email tidak valid: ${invalid.join(", ")}`);
      }
    }

    if (validationErrors.length > 0) {
      alert(
        `Beberapa data tidak valid dan TIDAK ditambahkan ke queue:\n\n${validationErrors.join("\n")}\n\nSilakan perbaiki lalu tambahkan ulang jika diperlukan.`
      );
    }

    if (!queueName && !action && finalJobIds.length === 0 && finalJobPlaceRefs.length === 0 &&
      finalUserIds.length === 0 && finalUserEmails.length === 0) return;

    if (!action) {
      alert("Silakan pilih Action terlebih dahulu.");
      return;
    }

    const newQueue: QueueItem = {
      name: queueName || "(Unnamed Queue)",
      jobs: jobInputMethod === "placeRef" ? finalJobPlaceRefs.length : finalJobIds.length,
      users: userInputMethod === "email" ? finalUserEmails.length : finalUserIds.length,
      jobIds: finalJobIds,
      jobPlaceRefs: finalJobPlaceRefs.length > 0 ? finalJobPlaceRefs : undefined,
      userIds: userInputMethod === "email" ? [] : finalUserIds,
      userEmails: userInputMethod === "email" ? finalUserEmails : [],
      action: action.toLowerCase(),
      jobInputMethod: jobInputMethod,
      userInputMethod: userInputMethod,
    };

    if (selectedQueueIndex !== null) {
      const updated = [...queues];
      updated[selectedQueueIndex] = newQueue;
      setQueues(updated);
      setSelectedQueueIndex(null);
    } else {
      setQueues([...queues, newQueue]);
    }

    setQueueName("");
    setAction("");

    if (jobInputMethod === "selection") {
      setSelectedJobIds([]);
    } else if (jobInputMethod === "id") {
      setManualJobIds("");
    } else if (jobInputMethod === "placeRef") {
      setManualJobPlaceRefs("");
    }

    if (userInputMethod === "selection") {
      setSelectedUserIds([]);
    } else if (userInputMethod === "id") {
      setManualUserIds("");
    } else if (userInputMethod === "email") {
      setManualUserEmails("");
    }
  };


  const handleEditQueue = (index: number) => {
    const q = queues[index];
    setQueueName(q.name === "(Unnamed Queue)" ? "" : q.name);
    setAction(q.action);

    const jobMethod = q.jobInputMethod || (q.jobIds && q.jobIds.length > 0 ? "selection" : "id");
    setJobInputMethod(jobMethod);

    if (jobMethod === "id") {
      setManualJobIds(q.jobIds?.join(", ") || "");
      setSelectedJobIds([]);
      setManualJobPlaceRefs("");
    } else if (jobMethod === "placeRef") {
      setManualJobPlaceRefs(q.jobPlaceRefs?.join(", ") || "");
      setSelectedJobIds([]);
      setManualJobIds("");
    } else {
      setSelectedJobIds(q.jobIds || []);
      setManualJobIds("");
      setManualJobPlaceRefs("");
    }

    const userMethod =
      q.userInputMethod ||
      (q.userEmails && q.userEmails.length > 0
        ? "email"
        : q.userIds && q.userIds.length > 0
          ? "id"
          : "selection");

    setUserInputMethod(userMethod);

    if (userMethod === "email") {
      setManualUserEmails(q.userEmails?.join(", ") || "");
      setManualUserIds("");
      setSelectedUserIds([]);
    } else if (userMethod === "id") {
      setManualUserIds(q.userIds?.join(", ") || "");
      setManualUserEmails("");
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(q.userIds || []);
      setManualUserIds("");
      setManualUserEmails("");
    }

    setSelectedQueueIndex(index);
  };


  const handleRemoveQueue = (index: number) => {
    setQueues(queues.filter((_, i) => i !== index));
  };

  const handleMethodChange = (newMethod: "xlsx" | "selected") => {
    if (method === newMethod) return;

    if (queues.length > 0) {
      const confirmReset = window.confirm(
        "Mengganti metode akan menghapus seluruh queue yang sudah dibuat. Lanjutkan?"
      );
      if (!confirmReset) return;
    }

    setQueues([]);
    setSelectedQueueIndex(null);
    setQueueName("");
    setAction("");
    setSelectedJobIds([]);
    setManualJobIds("");
    setManualJobPlaceRefs("");
    setSelectedUserIds([]);
    setManualUserIds("");
    setManualUserEmails("");
    setJobInputMethod("selection");
    setUserInputMethod("selection");

    setMethod(newMethod);
  };

  const handleNext = async () => {
    const token = profile?.data?.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    if (!id) {
      window.alert('Project ID tidak ditemukan.');
      return;
    }

    setIsNextLoading(true);
    setPreviewProgress(null);

    if (method === "xlsx") {
      if (uploadedData.length === 0) {
        alert("Silakan upload file XLSX terlebih dahulu sebelum melanjutkan.");
        setIsNextLoading(false);
        return;
      }

      const atomicItems: AtomicAssignmentItem[] = uploadedData.map((row) => ({
        jobId: row.job_id !== undefined && row.job_id !== null && row.job_id !== "" ? String(row.job_id).trim() : undefined,
        userId: row.user_id !== undefined && row.user_id !== null && row.user_id !== "" ? String(row.user_id).trim() : undefined,
        action: (row.action ? String(row.action) : "add").toLowerCase(),
      }));

      if (atomicItems.length > MAX_TOTAL_ROWS) {
        alert(`Jumlah data (${atomicItems.length}) melebihi batas maksimum ${MAX_TOTAL_ROWS} baris. Silakan kurangi jumlah data.`);
        setIsNextLoading(false);
        return;
      }

      // Pisahkan baris dengan format ID salah (typo, karakter aneh, dll) sebelum
      // dikirim ke API. jobIds/userIds di BE bertipe uuid.UUID, jadi 1 baris
      // dengan format salah bisa membuat SELURUH chunk (200 baris) gagal
      // binding (400), bukan cuma baris itu sendiri. Baris ini tetap tampil di
      // step 2 sebagai "not valid" dengan alasan jelas, bukan menghentikan
      // seluruh proses.
      const malformedResults: any[] = [];
      const sendableItems: AtomicAssignmentItem[] = [];

      atomicItems.forEach((item) => {
        const jobOk = isValidUuid(item.jobId);
        const userOk = isValidUuid(item.userId);

        if (!jobOk || !userOk) {
          const reasons: string[] = [];
          if (!jobOk) reasons.push("Format job_id tidak valid (harus UUID)");
          if (!userOk) reasons.push("Format user_id tidak valid (harus UUID)");

          malformedResults.push({
            jobId: item.jobId || "-",
            jobName: "-",
            jobDescription: "-",
            fieldUserEmail: "-",
            fieldUserName: "-",
            action: item.action,
            checkValid: false,
            invalidReason: reasons,
          });
        } else {
          sendableItems.push(item);
        }
      });

      try {
        setPreviewProgress({ current: 0, total: sendableItems.length });

        const data = await processInChunks(
          sendableItems,
          INITIAL_CHUNK_SIZE,
          (done, total) => setPreviewProgress({ current: done, total }),
          async (chunk) => {
            const payload = {
              assignmentQueue: chunk.map((item) => ({
                userIds: item.userId ? [item.userId] : [],
                jobIds: item.jobId ? [item.jobId] : [],
                action: item.action,
              })),
            };
            const res = await projectService.checkPreviewAssignment(token, id, payload);
            return res.data || [];
          }
        );

        const combined = [...malformedResults, ...data];
        setPreviewData(combined);
        setFilteredData(combined);
        setFilter("all");
        setCurrentPage(1);
        setStep(2);
      } catch (err: any) {
        console.error("Preview failed:", err);
        const errorMessage = err.response?.data?.message || err.message || 'Gagal memuat preview assignment dari file XLSX';
        alert(`Error: ${errorMessage}`);
      } finally {
        setIsNextLoading(false);
        setPreviewProgress(null);
      }
    }
    else if (method === "selected") {
      if (queues.length === 0) {
        alert("Tambahkan minimal satu queue sebelum melanjutkan.");
        setIsNextLoading(false);
        return;
      }

      const atomicItems = buildAtomicItemsFromQueues(queues);

      if (atomicItems.length === 0) {
        alert("Tidak ada kombinasi job dan user yang valid pada queue.");
        setIsNextLoading(false);
        return;
      }

      if (atomicItems.length > MAX_TOTAL_ROWS) {
        alert(`Estimasi total assignment (${atomicItems.length}) melebihi batas maksimum ${MAX_TOTAL_ROWS}. Silakan kurangi jumlah job/user pada queue.`);
        setIsNextLoading(false);
        return;
      }

      const malformedResults: any[] = [];
      const sendableItems: AtomicAssignmentItem[] = [];

      atomicItems.forEach((item) => {
        const jobOk = item.jobPlaceRef ? item.jobPlaceRef.trim().length > 0 : isValidUuid(item.jobId);
        const userOk = item.userEmail ? isValidEmail(item.userEmail) : isValidUuid(item.userId);

        if (!jobOk || !userOk) {
          const reasons: string[] = [];
          if (!jobOk) reasons.push(item.jobPlaceRef ? "Place reference kosong" : "Format job_id tidak valid (harus UUID)");
          if (!userOk) reasons.push(item.userEmail ? "Format email tidak valid" : "Format user_id tidak valid (harus UUID)");

          malformedResults.push({
            jobId: item.jobId || item.jobPlaceRef || "-",
            jobName: "-",
            jobDescription: "-",
            fieldUserEmail: item.userEmail || "-",
            fieldUserName: "-",
            action: item.action,
            checkValid: false,
            invalidReason: reasons,
          });
        } else {
          sendableItems.push(item);
        }
      });

      try {
        setPreviewProgress({ current: 0, total: sendableItems.length });

        const data = await processInChunks(
          sendableItems,
          INITIAL_CHUNK_SIZE,
          (done, total) => setPreviewProgress({ current: done, total }),
          async (chunk) => {
            const payload = {
              assignmentQueue: chunk.map((item) => {
                const entry: any = { action: item.action };

                if (item.jobPlaceRef) {
                  entry.placeRefIds = [item.jobPlaceRef];
                } else {
                  entry.jobIds = item.jobId ? [item.jobId] : [];
                }

                if (item.userEmail) {
                  entry.userEmails = [item.userEmail];
                } else {
                  entry.userIds = item.userId ? [item.userId] : [];
                }

                return entry;
              }),
            };
            const res = await projectService.checkPreviewAssignment(token, id, payload);
            return res.data || [];
          }
        );

        const combined = [...malformedResults, ...data];
        setPreviewData(combined);
        setFilteredData(combined);
        setFilter("all");
        setCurrentPage(1);
        setStep(2);
      } catch (err: any) {
        console.error("Preview failed:", err);
        const errorMessage = err.response?.data?.message || err.message || 'Gagal memuat preview assignment';
        alert(`Error: ${errorMessage}`);
      } finally {
        setIsNextLoading(false);
        setPreviewProgress(null);
      }
    }
    else {
      alert("Silahkan import file XLSX terlebih dahulu");
      setIsNextLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    const lower = value.toLowerCase();

    const result = previewData.filter((item) => {
      const searchValue = lower;
      return (
        String(item.jobId ?? "").toLowerCase().includes(searchValue) ||
        String(item.jobName ?? "").toLowerCase().includes(searchValue) ||
        String(item.fieldUserEmail ?? "").toLowerCase().includes(searchValue) ||
        String(item.fieldUserName ?? "").toLowerCase().includes(searchValue) ||
        String(item.action ?? "").toLowerCase().includes(searchValue)
      );
    });

    setFilteredData(result);
    setCurrentPage(1);
  };

  const handleFilter = (type: "all" | "valid" | "invalid") => {
    setFilter(type);
    setCurrentPage(1);

    let result = [...previewData];
    if (type === "valid") {
      result = previewData.filter((item) => item.checkValid === true);
    } else if (type === "invalid") {
      result = previewData.filter((item) => item.checkValid === false);
    }

    if (search.trim()) {
      const lower = search.toLowerCase();
      result = result.filter((item) =>
        String(item.jobId ?? "").toLowerCase().includes(lower) ||
        String(item.jobName ?? "").toLowerCase().includes(lower) ||
        String(item.fieldUserEmail ?? "").toLowerCase().includes(lower)
      );
    }

    setFilteredData(result);
  };

  const handleRemoveRow = (rowIndexInCurrentPage: number) => {
    const rowToRemove = currentRows[rowIndexInCurrentPage];

    if (!rowToRemove) return;
    const uniqueKey = `${rowToRemove.jobId}-${rowToRemove.fieldUserEmail}-${rowToRemove.action}-${rowToRemove.fieldUserId || ''}`;
    const newPreview = previewData.filter(item =>
      `${item.jobId}-${item.fieldUserEmail}-${item.action}-${item.fieldUserId || ''}` !== uniqueKey
    );
    setPreviewData(newPreview);
    const newFiltered = filteredData.filter(item =>
      `${item.jobId}-${item.fieldUserEmail}-${item.action}-${item.fieldUserId || ''}` !== uniqueKey
    );
    setFilteredData(newFiltered);
    if (newFiltered.length > 0 && currentRows.length === 1 && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));

  // Estimasi total assignment (job x user) dari seluruh queue, dipakai untuk
  // validasi batas 2000 baris dan ditampilkan ke user secara real-time.
  const estimatedTotalAssignment = queues.reduce(
    (sum, q) => sum + (q.jobs || 0) * (q.users || 0),
    0
  );

  useEffect(() => {
    let result = previewData;
    if (filter === "valid") result = previewData.filter((i) => i.checkValid === true);
    else if (filter === "invalid") result = previewData.filter((i) => i.checkValid === false);

    if (search.trim()) {
      const lower = search.toLowerCase();
      result = result.filter((item) =>
        String(item.jobId ?? "").toLowerCase().includes(lower) ||
        String(item.jobName ?? "").toLowerCase().includes(lower) ||
        String(item.fieldUserEmail ?? "").toLowerCase().includes(lower)
      );
    }

    setFilteredData(result);
    setCurrentPage((prev) => {
      const totalPagesAfterUpdate = Math.max(1, Math.ceil(result.length / rowsPerPage));
      return prev > totalPagesAfterUpdate ? totalPagesAfterUpdate : prev;
    });
  }, [previewData, filter, search, rowsPerPage]);

  const handleProceed = async () => {
    const processDataAll: ProjectTaskProceedJobAssignmentDataReq[] = previewData.map((item) => ({
      jobId: item.jobId,
      fieldUserId: item.fieldUserId,
      action: item.action as "add" | "replace" | "revoke",
    }));

    if (processDataAll.length === 0) return;

    if (processDataAll.length > MAX_TOTAL_ROWS) {
      alert(`Jumlah data (${processDataAll.length}) melebihi batas maksimum ${MAX_TOTAL_ROWS}. Silakan kurangi jumlah data.`);
      return;
    }

    let processedCount = 0;

    try {
      setIsProceeding(true);
      setProceedProgress({ current: 0, total: processDataAll.length });

      await processInChunks(
        processDataAll,
        INITIAL_CHUNK_SIZE,
        (done, total) => setProceedProgress({ current: done, total }),
        async (chunk) => {
          const payload: ProjectTaskProceedJobAssignmentReq = {
            projectId: id!,
            processData: chunk,
          };
          await projectService.proceedJobAssignment(profile?.data?.token, payload);

          processedCount += chunk.length;
          setPreviewData((prev) => prev.slice(chunk.length));

          return [];
        }
      );

      setStep(3);
    } catch (err: any) {
      console.error("Error proceed job assignment:", err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat memproses assignment';

      if (processedCount > 0) {
        window.alert(
          `${processedCount} dari ${processDataAll.length} data berhasil diproses sebelum terhenti.\n\nError pada data berikutnya: ${errorMessage}\n\nSisa data yang belum diproses tetap ada di tabel — silakan klik Proceed lagi untuk melanjutkan, atau perbaiki datanya dulu.`
        );
      } else {
        window.alert(`Error: ${errorMessage}`);
      }
    } finally {
      setIsProceeding(false);
      setProceedProgress(null);
    }
  };

  if (isLoading && step !== 2) {
    return (
      <div className="p-5 d-flex justify-content-center align-items-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="pt-1 h-100" style={{ background: "#f2f4f8", minHeight: "100vh" }}>
      <div className="m-4">
        <h4 className="mb-4 fw-bold">Project Detail - Advance Job Assignment</h4>

        <div className="p-4 bg-white rounded shadow-sm">
          <div className="mb-4">
            <p className="fw-semibold mb-1">Project Name</p>
            <p className="text-secondary">{project?.name || "-"}</p>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-5 mx-auto"
            style={{ maxWidth: "700px", position: "relative" }}>
            {["Select Job", "Preview Assignment", "Assignment Success"].map((label, index) => {
              const stepNumber = index + 1;
              const isActive = stepNumber === step;
              return (
                <React.Fragment key={label}>
                  <div
                    className="d-flex flex-column align-items-center text-center position-relative"
                    style={{ flex: 1 }}
                  >
                    {index > 0 && (
                      <div
                        className="stepper-line-active"
                        style={{
                          position: "absolute",
                          left: "-50%",
                          top: "20px",
                          height: "2px",
                          width: "100%",
                          backgroundColor: "#0d6efd",
                          zIndex: 0,
                        }}
                      ></div>
                    )}
                    <div
                      className={`d-flex align-items-center justify-content-center fw-bold mb-2 ${isActive
                        ? "stepper-circle-active"
                        : "border border-primary text-primary bg-white"
                        }`}
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        fontSize: "14px",
                        zIndex: 1,
                      }}
                    >
                      {stepNumber}
                    </div>
                    <span
                      className={`fw-semibold small ${isActive ? "text-primary" : "text-muted"
                        }`}
                    >
                      {label}
                    </span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {step === 1 && (
            <>
              <div className="mb-4">
                <p className="fw-semibold">Choose Method</p>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="method"
                    id="methodXlsx"
                    checked={method === "xlsx"}
                    onChange={() => handleMethodChange("xlsx")}
                  />
                  <label className="form-check-label" htmlFor="methodXlsx">
                    By XLSX
                  </label>
                </div>

                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="method"
                    id="methodSelected"
                    checked={method === "selected"}
                    onChange={() => handleMethodChange("selected")}
                  />
                  <label className="form-check-label" htmlFor="methodSelected">
                    By Selected ID
                  </label>
                </div>
                <div className="small text-muted mt-1">
                  Maksimum {MAX_TOTAL_ROWS} baris/assignment per proses.
                </div>
              </div>

              {method === "xlsx" ? (
                <>
                  <div className="mb-3">
                    <label className="fw-semibold mb-2">Upload File</label>
                    <div className="d-flex align-items-center">
                      <input
                        type="file"
                        accept=".xlsx"
                        onChange={handleFileChange}
                        className="form-control me-3"
                        style={{ maxWidth: "400px" }}
                      />
                    </div>
                    {uploadedFileName && (
                      <div className="small text-muted mt-2">
                        <i className="fa-solid fa-file-excel me-1"></i>
                        {uploadedFileName}
                      </div>
                    )}
                    {uploadedData.length > 0 && (
                      <div
                        className={`small mt-1 ${uploadedData.length > MAX_TOTAL_ROWS ? "text-danger fw-semibold" : "text-muted"}`}
                      >
                        Total data terupload: {uploadedData.length} / {MAX_TOTAL_ROWS} baris
                      </div>
                    )}
                  </div>
                  <Link
                    to="#"
                    className="text-primary text-decoration-none fw-semibold"
                    onClick={handleDownloadTemplate}
                  >
                    Download Template
                  </Link>
                </>
              ) : (
                <div className="row">
                  <div className="col-md-7">
                    <div className="row mb-3">
                      <div className="col-md-6 mb-3">
                        <label className="fw-semibold mb-2">Queue Name (Optional)</label>
                        <input
                          type="text"
                          className="form-control"
                          value={queueName}
                          onChange={(e) => setQueueName(e.target.value)}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="fw-semibold mb-2">Action</label>
                        <CustomDropdown
                          value={action}
                          options={ACTION_OPTIONS}
                          // onChange={(e) => setAction(e.target.value)}
                          onChange={(option) => setAction(String(option.id))}
                          containerClassName="w-100"
                          buttonClassName="form-control form-control w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <label className="fw-semibold mb-0">Job IDs</label>
                        <div ref={jobInputMethodNavRef} className="nav-pills-animated">
                          <div className="nav-pill-indicator" style={jobInputMethodIndicatorStyle} />
                          <button
                            type='button'
                            className={`nav-link btn-sm ${jobInputMethod === "selection" ? "active" : ""}`}
                            onClick={() => handleJobInputMethodChange("selection")}
                          >
                            Selection
                          </button>
                          <button
                            type='button'
                            className={`nav-link btn-sm ${jobInputMethod === "id" ? "active" : ""}`}
                            onClick={() => handleJobInputMethodChange("id")}
                          >
                            Input ID
                          </button>
                          <button
                            type='button'
                            className={`nav-link btn-sm ${jobInputMethod === "placeRef" ? "active" : ""}`}
                            onClick={() => handleJobInputMethodChange("placeRef")}
                          >
                            Input PlaceRef
                          </button>
                        </div>
                      </div>

                      {jobInputMethod === "selection" ? (
                        <div style={{ position: "relative" }}>
                          <MultiSelectDropdown
                            options={jobs.map(job => ({
                              code: job.id,
                              name: `${job.name}`,
                              description: job.description
                            }))}
                            value={selectedJobIds}
                            placeholder="Select Jobs"
                            onChange={setSelectedJobIds}
                            onSearchChange={(value) => setJobSearch(value)}
                            previewKey="name"
                            subKey="description"
                            stayOpenOnUpdate
                          />

                          {isLoadingJobs && (
                            <div
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: "rgba(255,255,255,0.7)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "0.25rem",
                                zIndex: 10,
                              }}
                            >
                              <div className="spinner-border spinner-border-sm text-primary"></div>
                              <span className="ms-2 text-primary">Loading jobs...</span>
                            </div>
                          )}
                        </div>
                      ) : jobInputMethod === "id" ? (
                        <textarea
                          className="form-control"
                          rows={3}
                          placeholder="job-1, job-2"
                          value={manualJobIds}
                          onChange={(e) => setManualJobIds(e.target.value)}
                          onPaste={(e) => {
                            e.preventDefault();
                            const pastedText = e.clipboardData.getData('text');
                            setManualJobIds(cleanPastedText(pastedText));
                          }}
                          onBlur={(e) => {
                            if (e.target.value.trim()) {
                              setManualJobIds(cleanInputIds(e.target.value));
                            }
                          }}
                        ></textarea>
                      ) : (
                        <textarea
                          className="form-control"
                          rows={3}
                          placeholder="place-ref-1, place-ref-2"
                          value={manualJobPlaceRefs}
                          onChange={(e) => setManualJobPlaceRefs(e.target.value)}
                          onPaste={(e) => {
                            e.preventDefault();
                            const pastedText = e.clipboardData.getData('text');
                            setManualJobPlaceRefs(cleanPastedText(pastedText));
                          }}
                          onBlur={(e) => {
                            if (e.target.value.trim()) {
                              setManualJobPlaceRefs(cleanInputIds(e.target.value));
                            }
                          }}
                        ></textarea>
                      )}
                      <div className="mt-2 text-muted small">
                        Selected: {jobInputMethod === "selection" ? selectedJobIds.length : jobInputMethod === "id" ? (manualJobIds ? manualJobIds.split(",").length : 0) : (manualJobPlaceRefs ? manualJobPlaceRefs.split(",").length : 0)} job(s)
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <label className="fw-semibold mb-0">User IDs</label>
                        <div ref={userInputMethodNavRef} className="nav-pills-animated">
                          <div className="nav-pill-indicator" style={userInputMethodIndicatorStyle} />
                          <button
                            className={`nav-link btn-sm ${userInputMethod === "selection" ? "active" : ""}`}
                            onClick={() => handleUserInputMethodChange("selection")}
                          >
                            Selection
                          </button>
                          <button
                            className={`nav-link btn-sm ${userInputMethod === "id" ? "active" : ""}`}
                            onClick={() => handleUserInputMethodChange("id")}
                          >
                            Input ID
                          </button>
                          <button
                            className={`nav-link btn-sm ${userInputMethod === "email" ? "active" : ""}`}
                            onClick={() => handleUserInputMethodChange("email")}
                          >
                            Input Email
                          </button>
                        </div>
                      </div>

                      {userInputMethod === "selection" ? (
                        <div style={{ position: "relative" }}>
                          <MultiSelectDropdown
                            options={fieldUsers.map(user => ({
                              code: user.id,
                              name: `${user.name}`,
                              description: `${user.email} | ${user.phone}`
                            }))}
                            value={selectedUserIds}
                            placeholder="Select Users"
                            onChange={setSelectedUserIds}
                            onSearchChange={(value) => setUserSearch(value)}
                            previewKey="name"
                            subKey="description"
                            stayOpenOnUpdate
                          />

                          {isLoadingUsers && (
                            <div
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: "rgba(255,255,255,0.7)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "0.25rem",
                                zIndex: 10,
                              }}
                            >
                              <div className="spinner-border spinner-border-sm text-primary"></div>
                              <span className="ms-2 text-primary">Loading users...</span>
                            </div>
                          )}
                        </div>
                      ) : userInputMethod === "id" ? (
                        <textarea
                          className="form-control"
                          rows={3}
                          placeholder="user-1, user-2"
                          value={manualUserIds}
                          onChange={(e) => setManualUserIds(e.target.value)}
                          onPaste={(e) => {
                            e.preventDefault();
                            const pastedText = e.clipboardData.getData('text');
                            setManualUserIds(cleanPastedText(pastedText));
                          }}
                          onBlur={(e) => {
                            if (e.target.value.trim()) {
                              setManualUserIds(cleanInputIds(e.target.value));
                            }
                          }}
                        ></textarea>
                      ) : (
                        <textarea
                          className="form-control"
                          rows={3}
                          placeholder="email1@example.com, email2@example.com"
                          value={manualUserEmails}
                          onChange={(e) => setManualUserEmails(e.target.value)}
                          onPaste={(e) => {
                            e.preventDefault();
                            const pastedText = e.clipboardData.getData('text');
                            setManualUserEmails(cleanPastedText(pastedText));
                          }}
                          onBlur={(e) => {
                            if (e.target.value.trim()) {
                              setManualUserEmails(cleanInputIds(e.target.value));
                            }
                          }}
                        ></textarea>
                      )}
                      <div className="mt-2 text-muted small">
                        Selected: {userInputMethod === "selection" ? selectedUserIds.length : userInputMethod === "id" ? (manualUserIds ? manualUserIds.split(",").length : 0) : (manualUserEmails ? manualUserEmails.split(",").length : 0)} user(s)
                      </div>
                    </div>

                    <div className="text-end mb-4">
                      <button className="btn btn-primary" onClick={handleAddQueue}>
                        Add to Queue
                      </button>
                    </div>
                  </div>

                  <div className="col-md-5">
                    <p className="fw-semibold mb-3">Queue Preview</p>
                    {queues.length === 0 ? (
                      <div className="text-muted fst-italic">No queue added</div>
                    ) : (
                      <div className="queue-list pe-2" style={{ maxHeight: "400px", overflowY: "auto" }}>
                        {queues.map((q, i) => (
                          <div
                            key={i}
                            className={`d-flex justify-content-between align-items-center px-3 py-2 rounded mb-2 ${selectedQueueIndex === i ? "bg-success text-white" : "bg-primary text-white"
                              }`}
                            style={{ cursor: "pointer" }}
                            onClick={() => handleEditQueue(i)}
                          >
                            <span className="fw-semibold">{q.name}</span>
                            <div className="d-flex align-items-center">
                              <div className="text-end me-2">
                                <small className="d-block fw-semibold">
                                  {q.users} user, {q.jobs} job
                                </small>
                                <small className="d-block text-capitalize">
                                  {q.action}
                                </small>
                              </div>
                              <button
                                className="btn btn-outline-light btn-small-compact border-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveQueue(i);
                                }}
                              >
                                <i className="fa-solid fa-xmark"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {queues.length > 0 && (
                      <div className="mt-2 text-muted text-end px-2">
                        <span className="d-block">
                          Total: {queues.reduce((sum, q) => sum + (q.jobs || 0), 0)} Job(s)
                        </span>
                        <span
                          className={`d-block ${estimatedTotalAssignment > MAX_TOTAL_ROWS ? "text-danger fw-semibold" : ""
                            }`}
                        >
                          Estimasi assignment: {estimatedTotalAssignment} / {MAX_TOTAL_ROWS}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {previewProgress && (
                <div className="mb-3 mt-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span className="fw-semibold small">Memproses data preview...</span>
                    <span className="small">
                      {previewProgress.current}/{previewProgress.total} (
                      {previewProgress.total > 0
                        ? Math.round((previewProgress.current / previewProgress.total) * 100)
                        : 0}
                      %)
                    </span>
                  </div>
                  <div className="progress" style={{ height: "8px" }}>
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{
                        width: `${previewProgress.total > 0
                          ? (previewProgress.current / previewProgress.total) * 100
                          : 0
                          }%`,
                      }}
                    ></div>
                  </div>
                </div>
              )}

              <div className="d-flex justify-content-end mt-4 gap-2">
                <button className="btn btn-outline-primary" onClick={() => navigate(`/project/detail/${id}`)}>
                  Back
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleNext}
                  disabled={
                    isNextLoading ||
                    (
                      method === "selected" && (
                        selectedQueueIndex !== null ||
                        queues.length === 0 ||
                        estimatedTotalAssignment > MAX_TOTAL_ROWS ||
                        (
                          queueName.trim() !== "" ||
                          action.trim() !== "" ||
                          selectedJobIds.length > 0 ||
                          manualJobIds.trim() !== "" ||
                          manualJobPlaceRefs.trim() !== "" ||
                          selectedUserIds.length > 0 ||
                          manualUserIds.trim() !== "" ||
                          manualUserEmails.trim() !== ""
                        )
                      )
                    )
                    ||
                    (
                      method === "xlsx" && (uploadedData.length === 0 || uploadedData.length > MAX_TOTAL_ROWS)
                    )
                  }
                >
                  {isNextLoading ? (
                    <span className="d-flex align-items-center">
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      {previewProgress
                        ? `Memproses ${previewProgress.current}/${previewProgress.total}`
                        : "Loading..."}
                    </span>
                  ) : (
                    "Next"
                  )}
                </button>
              </div>
            </>
          )}

          {step === 2 && (
            <div>
              <h5 className="fw-bold mb-3">Preview Assignment</h5>

              {isPreviewLoading ? (
                <div className="text-center py-5">
                  <LoadingSpinner />
                  <p className="text-muted mt-2">Loading preview...</p>
                </div>
              ) : (
                <>
                  <div className="d-flex align-items-center mb-3">
                    <form
                      className="d-flex align-items-center"
                      onSubmit={(e) => {
                        e.preventDefault();
                        setCurrentPage(1);
                      }}
                    >
                      <input
                        type="text"
                        className="form-control me-2"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => handleSearch(e.target.value)}
                        style={{
                          width: "350px",
                          height: "50px",
                        }}
                      />
                      <button
                        type="submit"
                        className="btn btn-primary"
                        style={{ height: "50px" }}
                        onClick={() => setCurrentPage(1)}
                      >
                        <i className="fa-solid fa-search"></i>
                      </button>
                    </form>

                    <div className="d-flex justify-content-end ms-auto" style={{ flex: 1 }}>
                      <ul className="nav nav-pills h-100">
                        <li className="nav-item m-0 h-100">
                          <button
                            className={`h-100 nav-link fw-semibold ${filter === "all" ? "active" : ""}`}
                            onClick={() => handleFilter("all")}
                          >
                            All
                          </button>
                        </li>
                        <li className="nav-item m-0 h-100">
                          <button
                            className={`h-100 nav-link fw-semibold ${filter === "valid" ? "active" : ""}`}
                            onClick={() => handleFilter("valid")}
                          >
                            Valid
                          </button>
                        </li>
                        <li className="nav-item m-0 h-100">
                          <button
                            className={`h-100 nav-link fw-semibold position-relative ${filter === "invalid" ? "active" : ""}`}
                            onClick={() => handleFilter("invalid")}
                          >
                            Not Valid
                            {invalidCount > 0 && (
                              <span
                                className="position-absolute badge rounded-pill bg-danger"
                                style={{ top: "-6px", right: "-6px", fontSize: "0.65rem" }}
                                title={`${invalidCount} baris tidak valid`}
                              >
                                {invalidCount}
                              </span>
                            )}
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <table className="table table-condensed table-hover">
                    <thead>
                      <tr>
                        <th style={{ width: "220px" }} className="text-start">Job ID</th>
                        <th className="text-center">Job Name</th>
                        <th className="text-center">Job Description</th>
                        <th className="text-center">Field User Email</th>
                        <th className="text-center">Field User Name</th>
                        <th className="text-center">Action</th>
                        <th className="text-center">Check Valid</th>
                        <th></th>
                      </tr>
                    </thead>

                    <tbody>
                      {!isPreviewLoading ? (
                        currentRows.length > 0 ? (
                          currentRows.map((row, i) => (
                            <tr key={i}>
                              <td className="text-start">{row.jobId}</td>
                              <td className="text-center">{row.jobName || "-"}</td>
                              <td className="text-center">{row.jobDescription || "-"}</td>
                              <td className="text-center">{row.fieldUserEmail || "-"}</td>
                              <td className="text-center">{row.fieldUserName || "-"}</td>
                              <td className="text-center text-capitalize">{row.action}</td>

                              <td className="text-center">
                                {row.checkValid ? (
                                  <span className="text-success fw-semibold">valid</span>
                                ) : (
                                  <span className="d-inline-flex align-items-center">
                                    not valid&nbsp;
                                    <div
                                      className="tooltip-trigger d-inline-flex align-items-center position-relative"
                                      onMouseEnter={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const tooltip = document.createElement("div");
                                        tooltip.className = "global-tooltip";
                                        tooltip.innerText = Array.isArray(row.invalidReason)
                                          ? row.invalidReason.join(", ")
                                          : row.invalidReason || "Unknown reason";
                                        document.body.appendChild(tooltip);

                                        const top = rect.top + window.scrollY - tooltip.offsetHeight - 8;
                                        const left = rect.left + window.scrollX + rect.width / 2 - tooltip.offsetWidth / 2;
                                        tooltip.style.top = `${top}px`;
                                        tooltip.style.left = `${left}px`;
                                      }}
                                      onMouseLeave={() => {
                                        document.querySelectorAll(".global-tooltip").forEach((el) => el.remove());
                                      }}
                                    >
                                      <i className="fa-solid fa-circle-info text-dark" style={{ cursor: "pointer" }}></i>
                                    </div>
                                  </span>
                                )}
                              </td>

                              <td className="text-center">
                                {row.checkValid ? null : (
                                  <span
                                    className="text-danger fw-semibold"
                                    role="button"
                                    title="Remove this row"
                                    onClick={() => handleRemoveRow(i)}
                                  >
                                    <i className="fa-solid fa-circle-xmark text-danger"></i>
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={7} className="text-center text-muted py-4">No data found</td>
                          </tr>
                        )
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center py-4">
                            <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  <div className="d-flex justify-content-end align-items-center mt-3">
                    <div className="d-flex align-items-center">
                      <div className="me-4 d-flex align-items-center">
                        <label className="me-2">Rows per page:</label>
                        <CustomDropdown
                          value={rowsPerPage}
                          options={PageSizeOption.options}
                          onChange={(opt) => {
                            setRowsPerPage(Number(opt.id));
                            setCurrentPage(1);
                          }}
                          align="top"
                        />
                      </div>

                      <span className="me-3">
                        {`${filteredData.length === 0 ? 0 : (indexOfFirstRow + 1)}–${Math.min(indexOfLastRow, filteredData.length)} of ${filteredData.length}`}
                      </span>

                      <nav>
                        <ul className="pagination mb-0">
                          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                            <button className="page-link" onClick={() => setCurrentPage(1)} style={{ border: 0 }}>
                              <i className="fa fa-angle-double-left"></i>
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                            <button className="page-link" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} style={{ border: 0 }}>
                              <i className="fa fa-angle-left"></i>
                            </button>
                          </li>
                          <li className="page-item disabled">
                            <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                          </li>
                          <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                            <button className="page-link" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} style={{ border: 0 }}>
                              <i className="fa fa-angle-right"></i>
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                            <button className="page-link" onClick={() => setCurrentPage(totalPages)} style={{ border: 0 }}>
                              <i className="fa fa-angle-double-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>

                  {proceedProgress && (
                    <div className="mb-3 mt-4">
                      <div className="d-flex justify-content-between mb-1">
                        <span className="fw-semibold small">Memproses assignment...</span>
                        <span className="small">
                          {proceedProgress.current}/{proceedProgress.total} (
                          {proceedProgress.total > 0
                            ? Math.round((proceedProgress.current / proceedProgress.total) * 100)
                            : 0}
                          %)
                        </span>
                      </div>
                      <div className="progress" style={{ height: "8px" }}>
                        <div
                          className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                          role="progressbar"
                          style={{
                            width: `${proceedProgress.total > 0
                              ? (proceedProgress.current / proceedProgress.total) * 100
                              : 0
                              }%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {invalidCount > 0 && (
                    <div className="alert alert-warning d-flex justify-content-between align-items-center mt-3 mb-0 py-2 px-3">
                      <span>
                        <i className="fa-solid fa-triangle-exclamation me-2"></i>
                        Ada <strong>{invalidCount}</strong> baris tidak valid (mungkin ada di halaman lain). Hapus atau perbaiki dulu sebelum bisa Proceed.
                      </span>
                      <button
                        className="btn btn-sm btn-outline-dark ms-3"
                        onClick={() => handleFilter("invalid")}
                      >
                        Lihat
                      </button>
                    </div>
                  )}

                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <button className="btn btn-outline-primary" onClick={() => setStep(1)} disabled={isProceeding}>
                      Back
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleProceed}
                      disabled={
                        filteredData.length === 0 ||
                        filteredData.some((item) => item.checkValid === false) ||
                        isProceeding
                      }
                    >
                      {isProceeding ? (
                        <span className="d-flex align-items-center">
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          {proceedProgress
                            ? `Memproses ${proceedProgress.current}/${proceedProgress.total}`
                            : "Processing..."}
                        </span>
                      ) : (
                        "Proceed"
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="text-center py-5">
              <i className="fa-solid fa-circle-check text-primary" style={{ fontSize: "8rem" }}></i>
              <h5 className="text-primary mt-5">Assignment Success</h5>
              <button className="btn btn-primary mt-3" onClick={() => navigate(`/project/detail/${id}`)}>
                Back to Project Job
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectAdvanceAssignment;
